import { WebLayout } from 'components/Layout';
import React, { useEffect } from 'react';
import jonahImg from 'assets/jonah.jpg';

export const Company = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <WebLayout title="Company" description="About Dejunk">
      <div className="relative overflow-hidden">
        {/* HERO */}
        <section className="bg-gradient-to-br from-gray-50 via-white to-gray-100 py-24">
          <div className="max-w-5xl mx-auto px-4 text-center">
            <h1 className="text-4xl md:text-5xl font-bold leading-tight">
              We’re building a new kind of marketplace.
            </h1>

            <p className="mt-6 text-gray-600 max-w-2xl mx-auto text-lg">
              A location-first marketplace that helps people turn clutter into opportunity quickly,
              locally, and without complexity.
            </p>
          </div>
        </section>

        {/* MISSION */}
        <section className="py-24">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-semibold">Our Mission</h2>

            <p className="mt-6 text-gray-600 leading-relaxed">
              Most marketplaces are noisy, slow, and overloaded with irrelevant listings. Dejunk
              removes the friction no endless scrolling, no complicated chats, no uncertainty. Just
              people nearby, real items, and direct contact.
            </p>

            <p className="mt-4 text-gray-600 leading-relaxed">
              We believe value shouldn’t sit idle. Every unused item has a second life and every
              home deserves more space, not more clutter.
            </p>
          </div>
        </section>

        {/* STORY */}
        <section className="bg-gray-50 py-24">
          <div className="max-w-5xl mx-auto px-4 grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-semibold">Why we built Dejunk</h2>

              <p className="mt-6 text-gray-600 leading-relaxed">
                We saw a simple problem: people have things they no longer need, and others nearby
                are actively looking for those exact things but the systems connecting them are
                broken.
              </p>

              <p className="mt-4 text-gray-600 leading-relaxed">
                So we removed everything unnecessary: messaging systems, global feeds, complex
                negotiation layers and replaced it with proximity and speed.
              </p>

              <p className="mt-4 text-gray-600 leading-relaxed">
                The result is Dejunk, a marketplace that feels instant.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 border">
              <div className="text-sm text-gray-400 mb-2">CORE PRINCIPLE</div>
              <div className="text-2xl font-semibold">
                “If it doesn’t help you sell faster, it doesn’t belong.”
              </div>

              <div className="mt-6 text-gray-600 text-sm">
                Every feature in Dejunk is optimized for one thing: reducing time between listing
                and contact.
              </div>
            </div>
          </div>
        </section>

        {/* VALUES */}
        <section className="py-24">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-semibold">What we stand for</h2>

            <div className="grid md:grid-cols-3 gap-8 mt-12 text-left">
              <div className="p-6 border rounded-2xl hover:shadow-md transition">
                <div className="text-2xl mb-3">⚡</div>
                <h3 className="font-semibold">Speed over complexity</h3>
                <p className="text-sm text-gray-600 mt-2">
                  Every interaction should feel instant. No friction, no waiting.
                </p>
              </div>

              <div className="p-6 border rounded-2xl hover:shadow-md transition">
                <div className="text-2xl mb-3">📍</div>
                <h3 className="font-semibold">Local first</h3>
                <p className="text-sm text-gray-600 mt-2">
                  The best deals are around you not across the internet.
                </p>
              </div>

              <div className="p-6 border rounded-2xl hover:shadow-md transition">
                <div className="text-2xl mb-3">📞</div>
                <h3 className="font-semibold">Direct connection</h3>
                <p className="text-sm text-gray-600 mt-2">
                  No middlemen. Buyers and sellers talk directly.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* VISION */}
        <section className="bg-black text-white py-24">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl font-semibold">Our vision</h2>

            <p className="mt-6 text-gray-300 leading-relaxed">
              We’re building the most efficient way to move unused goods locally where discovery is
              not global, but intentional and nearby.
            </p>

            <p className="mt-4 text-gray-300 leading-relaxed">
              In the future, every city block becomes its own marketplace.
            </p>

            <div className="mt-10">
              <span className="px-6 py-3 bg-white text-lg text-black rounded-xl font-black">
                Dejunk is just getting started!
              </span>
            </div>
          </div>

          {/* VISION BEARER */}
          <section className="py-24 px-12 md:px-28 flex flex-col md:flex-row md:space-x-12">
            <div className="mb-8 md:mb-0">
              <img
                src={jonahImg}
                alt="Vision Bearer"
                className=" h-48 w-48 md:h-56 md:w-56 object-cover rounded-3xl shadow-xl border"
              />
            </div>
            <div>
              <div className="text-sm text-gray-400 mb-2 uppercase tracking-wider">
                Vision Bearer
              </div>

              <h3 className="text-2xl font-semibold leading-tight">
                Building systems that respect time, space, and proximity.
              </h3>

              <p className="mt-6 text-gray-200 leading-relaxed">
                Dejunk is not just a marketplace. I think of it as rethinking how value moves within
                local communities. The goal is to eliminate friction and make exchange feel
                immediate, intentional, and human.
              </p>

              <p className="mt-2 text-gray-200 leading-relaxed">
                Every product decision is guided by one principle, which is to reduce the distance
                between intention and action!
              </p>

              <div className="mt-4 flex items-center gap-3">
                <div>
                  <div className="font-bold">Jonathan Mwebaze</div>
                  <div className="text-sm text-gray-200">Software Engineer, Founder</div>
                </div>
              </div>
            </div>
          </section>
        </section>

        {/* CTA */}
        <section className="py-24 text-center">
          <div className="max-w-xl mx-auto px-4">
            <h2 className="text-3xl font-semibold">Built for real-world simplicity</h2>

            <p className="text-gray-600 mt-4">
              Dejunk is designed to feel invisible until you need it.
            </p>

            <div className="mt-8">
              <a
                href="/#download"
                className="bg-black text-white px-6 py-3 rounded-xl font-medium hover:scale-105 transition inline-block"
              >
                Get the App
              </a>
            </div>
          </div>
        </section>
      </div>
    </WebLayout>
  );
};
